/**
 * V2WorkshopChat — The Workshop's soft onboarding chat + live team preview.
 * Shared by guided mode (inside Kindra OS) and the public self-serve page.
 * Token-scoped: all state loads from GET /api/workshop/{token} (save & resume).
 */
import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useSpringIn } from './motion';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const C = {
  navy: '#100838', surface: '#1A1150', hair: '#2A2070',
  icy: '#DCF0F5', muted: 'rgba(220,240,245,0.62)', faint: 'rgba(220,240,245,0.42)',
  aqua: '#34D1B0', magenta: '#EB258D',
  spectrum: 'linear-gradient(95deg,#34D1B0 0%,#1FB6C1 25%,#4F8FD0 50%,#9156C4 75%,#EB258D 100%)',
};

const Bubble = ({ role, children }) => (
  <div data-testid="workshop-message" className={`flex ${role === 'user' ? 'justify-end' : 'justify-start'} mb-3`}>
    <div className="max-w-[85%] px-4 py-3 text-[15px] leading-relaxed whitespace-pre-wrap"
         style={{
           fontFamily: 'Inter', color: C.icy,
           background: role === 'user' ? 'rgba(52,209,176,0.14)' : C.surface,
           border: `1px solid ${role === 'user' ? 'rgba(52,209,176,0.35)' : C.hair}`,
           borderRadius: role === 'user' ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
         }}>
      {children}
    </div>
  </div>
);

const Typing = () => (
  <div className="flex justify-start mb-3" data-testid="workshop-typing">
    <div className="px-4 py-3" style={{ background: C.surface, border: `1px solid ${C.hair}`, borderRadius: '16px 16px 16px 4px' }}>
      <span className="inline-flex gap-1">
        {[0, 1, 2].map((i) => (
          <span key={i} style={{
            width: 6, height: 6, borderRadius: 99, background: C.muted, display: 'inline-block',
            animation: `workshop-dot 1.2s ${i * 0.18}s infinite ease-in-out` }} />
        ))}
      </span>
    </div>
  </div>
);

const TeamCard = ({ t, accent }) => {
  const ref = useSpringIn(); // WOW — card springs in as the team takes shape
  const pending = t.fit === 'proposed';
  return (
  <div ref={ref} data-testid={`workshop-team-${t.id}`} className="rounded-xl px-4 py-3"
       style={{ background: accent === 'aqua' ? 'rgba(52,209,176,0.07)' : 'rgba(79,143,208,0.07)',
                border: `1px solid ${accent === 'aqua' ? 'rgba(52,209,176,0.28)' : 'rgba(79,143,208,0.28)'}`,
                opacity: pending ? 0.85 : 1 }}>
    <p className="text-[14px] font-bold" style={{ color: C.icy, fontFamily: 'Poppins' }}>
      <span style={{ color: accent === 'aqua' ? C.aqua : '#7FA9E0' }}>{pending ? '○' : '✓'}</span>&nbsp; {t.name}
      {pending && <span className="text-[10px] font-semibold ml-2 px-1.5 py-0.5 rounded-full"
        style={{ color: C.faint, border: `1px solid ${C.hair}`, fontFamily: 'Inter', verticalAlign: 'middle' }}>checking fit</span>}
    </p>
    {t.what && <p className="text-[12.5px] mt-0.5" style={{ color: C.muted, fontFamily: 'Inter' }}>{t.what}</p>}
  </div>
  );
};

const AddonsPanel = ({ addons }) => (
  (addons && addons.length > 0) ? (
    <div className="rounded-2xl p-5 mt-5" data-testid="workshop-addons-panel"
         style={{ background: 'rgba(240,195,109,0.06)', border: '1px solid rgba(240,195,109,0.30)' }}>
      <p className="text-[11px] font-bold uppercase tracking-[0.14em] mb-2" style={{ color: '#F0C36D', fontFamily: 'Inter' }}>
        Suggested add-ons — separate setup (upsell)
      </p>
      {addons.map((a) => (
        <div key={a.id} data-testid={`workshop-addon-${a.id}`} className="mb-2">
          <p className="text-[13.5px] font-bold" style={{ color: C.icy, fontFamily: 'Poppins' }}>⚡ {a.label}</p>
          {a.note && <p className="text-[12px]" style={{ color: C.muted, fontFamily: 'Inter' }}>{a.note}</p>}
        </div>
      ))}
      <p className="text-[11.5px] mt-1" style={{ color: C.faint, fontFamily: 'Inter' }}>
        Client was told these need real-world infrastructure Kindra sets up separately.
      </p>
    </div>
  ) : null
);

const TeamPanel = ({ team, checklist }) => {
  const foundation = team?.foundation || [];
  const custom = team?.custom || [];
  return (
  <div data-testid="workshop-team-panel" className="rounded-2xl p-5"
       style={{ background: C.surface, border: `1px solid ${C.hair}` }}>
    <div className="flex items-center gap-2 mb-1">
      <span style={{ display: 'inline-block', width: 22, height: 3, background: C.spectrum, borderRadius: 3 }} />
      <p className="text-[12px] font-bold uppercase tracking-[0.16em]" style={{ color: C.muted, fontFamily: 'Poppins' }}>
        Your team is building
      </p>
    </div>
    <p className="text-[12px] mb-4" style={{ color: C.faint, fontFamily: 'Inter' }}>
      Takes shape as we chat — a human reviews everything before anything's built.
    </p>

    {/* Layer 2 — custom specialists, the stars, on top */}
    <p className="text-[11px] font-bold uppercase tracking-[0.14em] mb-2" style={{ color: C.aqua, fontFamily: 'Inter' }}
       data-testid="workshop-custom-header">
      Built for you
    </p>
    {custom.length === 0 ? (
      <p className="text-[13px] mb-4" style={{ color: C.faint, fontFamily: 'Inter' }}>
        Specialists built just for your business appear here as we chat…
      </p>
    ) : (
      <div className="space-y-2.5 mb-4" data-testid="workshop-custom-list">
        {custom.map((t) => <TeamCard key={t.id} t={t} accent="aqua" />)}
      </div>
    )}

    {/* Layer 1 — the standard foundation, always present, tailored live */}
    <div className="pt-4" style={{ borderTop: `1px solid ${C.hair}` }}>
      <p className="text-[11px] font-bold uppercase tracking-[0.14em] mb-0.5" style={{ color: '#7FA9E0', fontFamily: 'Inter' }}
         data-testid="workshop-foundation-header">
        Standard team — every business gets these
      </p>
      <p className="text-[11.5px] mb-2.5" style={{ color: C.faint, fontFamily: 'Inter' }}>
        Tailored to you as we talk.
      </p>
      <div className="space-y-2.5" data-testid="workshop-foundation-list">
        {foundation.map((t) => <TeamCard key={t.id} t={t} accent="blue" />)}
      </div>
    </div>

    {checklist && checklist.length > 0 && (
      <div className="mt-5 pt-4" style={{ borderTop: `1px solid ${C.hair}` }}>
        <p className="text-[11px] font-bold uppercase tracking-[0.14em] mb-2" style={{ color: C.faint, fontFamily: 'Inter' }}>
          What we're learning
        </p>
        {checklist.map((c) => (
          <p key={c.key} className="text-[12.5px] mb-1" style={{ color: c.done ? C.icy : C.faint, fontFamily: 'Inter' }}>
            <span style={{ color: c.done ? C.aqua : C.faint }}>{c.done ? '✓' : '○'}</span>&nbsp; {c.label}
          </p>
        ))}
      </div>
    )}
  </div>
  );
};

export const V2WorkshopChat = ({ token, showAddons = false }) => {
  const [state, setState] = useState(null);
  const [err, setErr] = useState('');
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [quick, setQuick] = useState([]);
  const endRef = useRef(null);
  const fileRef = useRef(null);

  useEffect(() => {
    let alive = true;
    axios.get(`${API}/workshop/${token}`)
      .then((r) => { if (alive) setState(r.data); })
      .catch(() => { if (alive) setErr('This Workshop link looks invalid or has expired.'); });
    return () => { alive = false; };
  }, [token]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' }); },
    [state?.messages?.length, sending]);

  const applyResult = (res, userMsg) => {
    setState((s) => ({
      ...s,
      messages: [...s.messages,
        ...(userMsg ? [{ role: 'user', content: userMsg }] : []),
        { role: 'assistant', content: res.reply }],
      team: res.team ?? s.team,
      progress: res.progress ?? s.progress,
      checklist: res.checklist ?? s.checklist,
      suggested_addons: res.suggested_addons ?? s.suggested_addons,
      status: res.done ? 'complete' : s.status,
    }));
    setQuick(res.quick_replies || []);
  };

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || sending) return;
    setInput(''); setQuick([]); setSending(true);
    setState((s) => ({ ...s, messages: [...s.messages, { role: 'user', content: msg }] }));
    try {
      const r = await axios.post(`${API}/workshop/${token}/chat`, { message: msg });
      applyResult(r.data, null);
    } catch (e) {
      setState((s) => ({ ...s, messages: [...s.messages,
        { role: 'assistant', content: "Sorry — that didn't go through. Give it another try?" }] }));
    }
    setSending(false);
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f || sending) return;
    if (f.size > 8 * 1024 * 1024) { alert('Files up to 8MB please.'); return; }
    setSending(true);
    setState((s) => ({ ...s, messages: [...s.messages, { role: 'user', content: `📎 ${f.name}` }] }));
    try {
      const data_b64 = await new Promise((res, rej) => {
        const rd = new FileReader();
        rd.onload = () => res(String(rd.result).split(',')[1] || '');
        rd.onerror = rej;
        rd.readAsDataURL(f);
      });
      const r = await axios.post(`${API}/workshop/${token}/upload`,
        { filename: f.name, content_type: f.type, data_b64 });
      applyResult(r.data, null);
      setState((s) => ({ ...s, uploads: [...(s.uploads || []), { filename: f.name }] }));
    } catch {
      setState((s) => ({ ...s, messages: [...s.messages,
        { role: 'assistant', content: "That file didn't make it through — try again, or just describe it." }] }));
    }
    setSending(false);
  };

  if (err) return (
    <div className="rounded-2xl p-8 text-center" style={{ background: C.surface, border: `1px solid ${C.hair}` }}>
      <p style={{ color: C.icy, fontFamily: 'Inter' }}>{err}</p>
    </div>
  );
  if (!state) return (
    <p className="text-center py-16" style={{ color: C.faint, fontFamily: 'Inter' }}>Loading your Workshop…</p>
  );

  const complete = state.status === 'complete';
  const pct = state.progress?.pct ?? 0;

  return (
    <div className="w-full">
      <style>{`
        @keyframes workshop-dot { 0%,100% { opacity:.3; transform: translateY(0) } 50% { opacity:1; transform: translateY(-3px) } }
        .workshop-scroll::-webkit-scrollbar { width: 5px; }
        .workshop-scroll::-webkit-scrollbar-thumb { background: ${C.hair}; border-radius: 99px; }
      `}</style>

      {/* Calm progress */}
      <div data-testid="workshop-progress" className="mb-4">
        <div className="flex items-center justify-between mb-1.5">
          <p className="text-[12px]" style={{ color: C.muted, fontFamily: 'Inter' }}>
            {complete ? 'All done' : 'Quick setup chat'}
          </p>
          <p className="text-[12px]" style={{ color: C.faint, fontFamily: 'Inter' }}>{state.progress?.label}</p>
        </div>
        <div style={{ height: 4, background: 'rgba(220,240,245,0.08)', borderRadius: 99, overflow: 'hidden' }}>
          <div style={{ height: '100%', width: `${pct}%`, background: C.spectrum, borderRadius: 99,
                        transition: 'width 0.8s ease' }} />
        </div>
      </div>

      <div className="flex flex-col md:grid md:gap-6" style={{ gridTemplateColumns: 'minmax(0,1fr) 330px' }}>
        {/* Chat column */}
        <div className="rounded-2xl flex flex-col min-w-0"
             style={{ background: 'rgba(26,17,80,0.45)', border: `1px solid ${C.hair}` }}>
          <div className="workshop-scroll px-4 pt-5 pb-2 overflow-y-auto" style={{ maxHeight: '58vh', minHeight: 280 }}>
            {state.messages.map((m, i) => <Bubble key={i} role={m.role}>{m.content}</Bubble>)}
            {sending && <Typing />}
            <div ref={endRef} />
          </div>

          {/* Quick replies + easy outs */}
          {!complete && (
            <div className="px-4 pb-2 flex flex-wrap gap-2">
              {quick.map((q, i) => (
                <button key={i} data-testid={`workshop-quick-${i}`} onClick={() => send(q)} disabled={sending}
                        className="text-[12.5px] px-3 py-1.5 rounded-full"
                        style={{ color: C.icy, background: 'rgba(79,143,208,0.12)', border: '1px solid rgba(79,143,208,0.35)',
                                 fontFamily: 'Inter', cursor: 'pointer' }}>
                  {q}
                </button>
              ))}
              <button data-testid="workshop-skip-chip" onClick={() => send('Skip this one')} disabled={sending}
                      className="text-[12.5px] px-3 py-1.5 rounded-full"
                      style={{ color: C.muted, background: 'transparent', border: `1px solid ${C.hair}`,
                               fontFamily: 'Inter', cursor: 'pointer' }}>
                Skip this one
              </button>
              <button data-testid="workshop-notsure-chip" onClick={() => send("Not sure — you decide")} disabled={sending}
                      className="text-[12.5px] px-3 py-1.5 rounded-full"
                      style={{ color: C.muted, background: 'transparent', border: `1px solid ${C.hair}`,
                               fontFamily: 'Inter', cursor: 'pointer' }}>
                Not sure — you decide
              </button>
            </div>
          )}

          {/* Composer */}
          <div className="p-3 flex items-end gap-2" style={{ borderTop: `1px solid ${C.hair}` }}>
            <input ref={fileRef} type="file" className="hidden" data-testid="workshop-upload-input"
                   accept=".pdf,.png,.jpg,.jpeg,.webp,.txt,.md,.csv,.doc,.docx,.eml" onChange={onFile} />
            <button data-testid="workshop-upload-btn" title="Add a file — logo, price list, sample email…"
                    onClick={() => fileRef.current?.click()} disabled={sending || complete}
                    className="shrink-0 w-10 h-10 rounded-full flex items-center justify-center"
                    style={{ background: 'transparent', border: `1px solid ${C.hair}`, color: C.muted,
                             cursor: 'pointer', fontSize: 16 }}>
              📎
            </button>
            <textarea data-testid="workshop-chat-input" rows={1} value={input}
                      onChange={(e) => setInput(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
                      placeholder={complete ? 'This chat is wrapped up — Michael takes it from here.' : 'Type your answer…'}
                      disabled={sending || complete}
                      className="flex-1 resize-none px-4 py-2.5 text-[15px] outline-none min-w-0"
                      style={{ background: C.navy, border: `1px solid ${C.hair}`, borderRadius: 14,
                               color: C.icy, fontFamily: 'Inter' }} />
            <button data-testid="workshop-send-btn" onClick={() => send()} disabled={sending || complete || !input.trim()}
                    className="shrink-0 px-5 h-10 rounded-full text-[14px] font-bold"
                    style={{ background: (!input.trim() || sending || complete) ? 'rgba(235,37,141,0.35)' : C.spectrum,
                             color: '#fff', border: 'none', fontFamily: 'Poppins',
                             cursor: (!input.trim() || sending || complete) ? 'default' : 'pointer' }}>
              Send
            </button>
          </div>
        </div>

        {/* Preview column — below chat on mobile, side on desktop */}
        <div className="mt-5 md:mt-0">
          <TeamPanel team={state.team} checklist={state.checklist} />
          {showAddons && <AddonsPanel addons={state.suggested_addons} />}
        </div>
      </div>
    </div>
  );
};

export default V2WorkshopChat;
